<template>
  <v-card
    fill-height
    elevation="0"
    color="white"
    class="d-flex flex-column pa-4 card"
  >
    <div class="ma-0 pa-0 d-flex justify-space-between align-top">
      <EntityChip v-if="revType" :entityType="revType" :date="creationDate" />
      <RevStatus :rev="revStatus" showValidationIcons />
    </div>

    <span class="mt-4 h6 blue-grey--text text--darken-3"> {{ rev.name }}</span>

    <div class="imageContainer mt-4 pa-0 d-flex justify-center">
      <ImageMedia v-if="hasMedia" style="border:0;" :revMedia="media" />

      <ImageEmptyState
        v-else
        icon="mdi-image-outline"
        style="height:100%; width:100%"
      />
    </div>

    <div v-if="hasOwner || hasCoach" class="d-flex align-top mt-4">
      <div v-if="hasOwner">
        <span class="caption blue-grey--text text--darken-1 mt-2">
          {{ $t('common.owner_title') }}</span
        >
        <div class="d-flex mt-1">
          <ChipAvatar small :account="owner" />
        </div>
      </div>
      <div v-if="hasCoach" class="ml-3">
        <span class="caption blue-grey--text text--darken-1 mt-2">
          {{ $t('common.coach_title') }}</span
        >
        <div class="d-flex mt-1">
          <ChipAvatar small :account="coach" />
        </div>
      </div>
    </div>

    <div v-if="hasTeam" class="d-flex justify-start flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('rev_detail.collaborators') }}</span
      >
      <div class="mt-1">
        <ChipAvatar
          class="mr-1 mb-1"
          small
          v-for="member in team"
          :key="member.id"
          :account="member"
        />
      </div>
    </div>

    <div v-if="hasCategories" class="d-flex justify-start flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('column_selector.categories') }}</span
      >
      <div class="mt-1">
        <ChipList :items="categories" :limit="2" />
      </div>
    </div>

    <div v-if="hasTags" class="d-flex justify-start flex-column mt-2">
      <span class="caption blue-grey--text text--darken-1 mt-1">
        {{ $t('common.location_tags_options.tags') }}</span
      >
      <div class="mt-1">
        <ChipList :items="tags" :limit="2" />
      </div>
    </div>

    <div class="d-flex justify-start flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('common.location_title') }}</span
      >
      <div class="mt-1">
        <v-chip v-if="rev.site" data-private small outlined class="label chip">
          {{ rev.site }}
        </v-chip>
        <v-chip
          v-if="rev.country"
          data-private
          outlined
          small
          class="label ml-3 chip"
        >
          <v-icon size="16" color="blue-grey darken-2" class="mr-1"
            >mdi-earth</v-icon
          >
          {{ rev.country }}
        </v-chip>
      </div>
    </div>

    <div v-if="rev.problem" class="d-flex flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('rev_detail.problem_title') }}</span
      >
      <span class="mt-1 body-2 blue-grey--text text--darken-3">
        {{ rev.problem }}</span
      >
    </div>

    <div v-if="rev.solution" class="d-flex flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('rev_detail.solution_title') }}</span
      >
      <span class="mt-1 body-2 blue-grey--text text--darken-3">
        {{ rev.solution }}</span
      >
    </div>

    <div v-if="totalImpactText" class="d-flex flex-column mt-4">
      <span class="caption blue-grey--text text--darken-1">
        {{ $t('rev_detail.total_impact_title') }}</span
      >
      <span class="mt-1 body-2 blue-grey--text text--darken-3">
        {{ totalImpactText }}</span
      >
    </div>

    <span class="caption blue-grey--text text--darken-1 mt-4">
      {{ $t('rev_detail.impact_time_frame_title') }}</span
    >

    <div class="mt-1 d-flex align-top">
      <span class="body-2 blue-grey--text text--darken-3">
        {{ impactTimeFrameText }}</span
      >
    </div>
  </v-card>
</template>
<script>
import _ from 'lodash'

import ChipAvatar from '@/ui/shared/components/ChipAvatar.vue'
import EntityChip from '@/ui/shared/components/EntityChip.vue'
import ImageMedia from '@/ui/shared/components/ImageMediaView.vue'

import RevStatus from '@/ui/shared/components/RevStatus.vue'
import ChipList from '@/ui/shared/components/ChipList.vue'
import ImageEmptyState from '@/ui/shared/components/ImageEmptyState.vue'

export default {
  name: 'revDetailView',

  components: {
    ImageEmptyState,
    ChipAvatar,
    EntityChip,
    ImageMedia,
    RevStatus,
    ChipList,
  },

  props: {
    rev: {
      type: Object,
      required: true,
    },
  },

  computed: {
    revId() {
      return this.rev?.id ?? ''
    },

    revType() {
      return this.rev?.revType ?? this.rev?.entityType ?? ''
    },

    hasMedia() {
      return !_.isEmpty(this.rev.revMedia)
    },

    media() {
      return this.rev?.revMedia?.[0] ?? this.rev?.revMedia ?? null
    },

    owner() {
      return this.rev?.ownerObject
    },
    hasOwner() {
      return !_.isEmpty(this.owner)
    },

    coach() {
      return this.rev?.coachObject
    },

    hasCoach() {
      return !_.isEmpty(this.coach)
    },

    team() {
      return this.rev?.teamObject
    },

    hasTeam() {
      return !_.isEmpty(this.team)
    },

    hasCategories() {
      return !_.isEmpty(this.categories)
    },

    categories() {
      return this.rev?.categories
    },

    hasTags() {
      return !_.isEmpty(this.tags)
    },

    tags() {
      return this.rev?.tags ?? []
    },

    hasTotalImpact() {
      return !this.totalImpactText
    },

    totalImpactText() {
      return this.rev?.totalImpact
    },

    impactTimeFrameText() {
      return this.rev?.impactTimeFrame
    },

    revStatus() {
      return {
        status: this.rev?.statusId,
        validations: this.rev?.validations,
      }
    },

    creationDate() {
      return this.rev?.createdAt ?? ''
    },

    revMedia() {
      return this.rev?.revMedia
    },
  },

  methods: {
    async handleImport() {
      this.$emit('handleImport', this.revId)
    },
  },
}
</script>

<style lang="scss" scoped>
.imageContainer {
  border: 0 !important;
  height: 160px;
}

.card {
  border: 1px solid var(--v-blue-grey-lighten1) !important;
}

.chip:hover {
  background-color: transparent !important;
}
</style>
